import React, { useState, useEffect } from 'react';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Button } from '../../../Components/ui';
import Select from "react-select";
import moment from 'moment';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import CommonDatePicker from "@/Components/Common/CommonDatePicker";

const normalizeDayStart = (value) => moment(value).startOf('day');
const normalizeDayEnd = (value) => moment(value).endOf('day');

const BalanceOverview = ({ title, dataList, start_date, end_date, onChange = () => { },
    initialViewType = 'preset',  // 'preset', 'custom', 'yearly'
    initialSelectedRange = 7,
    viewSelectors = {
        viewType: true,
        dateRange: true,
        showDetails: true
    }
}) => {
    const [selectedRange, setSelectedRange] = useState(initialSelectedRange);
    const [showDetails, setShowDetails] = useState(false);
    const [viewType, setViewType] = useState(initialViewType); // 'preset', 'custom', 'yearly'
    const [customDateRange, setCustomDateRange] = useState([start_date || moment().subtract(7, 'days').format('YYYY-MM-DD'), end_date || moment().format('YYYY-MM-DD')]);
    const [selectedYear, setSelectedYear] = useState(moment().year());


    const selectDateRange = [
        { label: "오늘", value: 1 },
        { label: "지난1주", value: 7 },
        { label: "지난2주", value: 14 },
        { label: "지난1개월", value: 30 },
        { label: "지난3개월", value: 90 },
        { label: "지난1년", value: 356 },
    ]

    const viewTypeOptions = [
        { label: "기본 범위", value: "preset" },
        { label: "사용자 지정", value: "custom" },
        { label: "연도별", value: "yearly" }
    ];

    const yearOptions = [];
    for (let year = 2020; year <= moment().year(); year++) {
        yearOptions.push({ label: `${year}년`, value: year });
    }

    useEffect(() => {
        // Update chart data when dataList changes
        setChartData(getChartData());
    }, [dataList]);

    // Initialize chart data on mount
    useEffect(() => {
        if (dataList && dataList.length > 0) {
            setChartData(getChartData());
        }
    }, []);

    // Process API data for chart
    const processApiData = (apiData, days) => {
        const endDate = normalizeDayEnd(new Date());
        const startDate = normalizeDayStart(new Date()).subtract(days - 1, 'days');
        const dateRange = [];

        // Create all dates in range first
        for (let date = moment(startDate); date.isSameOrBefore(endDate, 'day'); date.add(1, 'day')) {
            dateRange.push({
                date: date.format('MM/DD'),
                fullDate: date.format('YYYY-MM-DD'),
                매출: 0,
                총수익: 0,
                취소금액: 0,
                예약수익: 0,
                티켓수익: 0,
                옵션수익: 0
            });
        }

        if (!apiData || apiData.length === 0) {
            // If no data, return empty date range
            return dateRange;
        }

        // Fill in actual data
        apiData
            .filter(item => {
                const itemDate = normalizeDayStart(item.date);
                return itemDate.isBetween(startDate, endDate, 'day', '[]');
            })
            .forEach(item => {
                const itemDate = normalizeDayStart(item.date);
                const dayIndex = itemDate.diff(startDate, 'days');

                if (dayIndex >= 0 && dayIndex < dateRange.length) {
                    const totalRevenue = (item.total_amount_info?.total_reserv || 0) +
                        (item.total_amount_info?.total_ticket || 0) +
                        (item.total_amount_info?.total_reserv_option || 0);
                    const totalCanceled = (item.total_amount_info?.total_cancel_reserv || 0) +
                        (item.total_amount_info?.total_cancel_ticket || 0) +
                        (item.total_amount_info?.total_cancel_reserv_option || 0);

                    dateRange[dayIndex].매출 = item.total_amount || 0;
                    dateRange[dayIndex].총수익 = totalRevenue;
                    dateRange[dayIndex].취소금액 = totalCanceled;
                    dateRange[dayIndex].예약수익 = item.total_amount_info?.total_reserv || 0;
                    dateRange[dayIndex].티켓수익 = item.total_amount_info?.total_ticket || 0;
                    dateRange[dayIndex].옵션수익 = item.total_amount_info?.total_reserv_option || 0;
                }
            });

        return dateRange;
    };

    // Process API data for custom date range
    const processCustomRangeData = (apiData, startDate, endDate) => {
        const start = normalizeDayStart(startDate);
        const end = normalizeDayEnd(endDate);
        const dateRange = [];

        // Create all dates in range first
        for (let date = moment(start); date.isSameOrBefore(end, 'day'); date.add(1, 'day')) {
            dateRange.push({
                date: date.format('MM/DD'),
                fullDate: date.format('YYYY-MM-DD'),
                매출: 0,
                총수익: 0,
                취소금액: 0,
                예약수익: 0,
                티켓수익: 0,
                옵션수익: 0
            });
        }

        if (!apiData || apiData.length === 0) {
            // If no data, return empty date range
            return dateRange;
        }

        // Fill in actual data
        apiData
            .filter(item => {
                const itemDate = normalizeDayStart(item.date);
                return itemDate.isBetween(start, end, 'day', '[]');
            })
            .forEach(item => {
                const itemDate = normalizeDayStart(item.date);
                const dayIndex = itemDate.diff(start, 'days');

                if (dayIndex >= 0 && dayIndex < dateRange.length) {
                    const totalRevenue = (item.total_amount_info?.total_reserv || 0) +
                        (item.total_amount_info?.total_ticket || 0) +
                        (item.total_amount_info?.total_reserv_option || 0);
                    const totalCanceled = (item.total_amount_info?.total_cancel_reserv || 0) +
                        (item.total_amount_info?.total_cancel_ticket || 0) +
                        (item.total_amount_info?.total_cancel_reserv_option || 0);

                    dateRange[dayIndex].매출 = item.total_amount || 0;
                    dateRange[dayIndex].총수익 = totalRevenue;
                    dateRange[dayIndex].취소금액 = totalCanceled;
                    dateRange[dayIndex].예약수익 = item.total_amount_info?.total_reserv || 0;
                    dateRange[dayIndex].티켓수익 = item.total_amount_info?.total_ticket || 0;
                    dateRange[dayIndex].옵션수익 = item.total_amount_info?.total_reserv_option || 0;
                }
            });

        return dateRange;
    };

    // Process API data for yearly view (aggregated by month)
    const processYearlyData = (apiData, year) => {
        const yearlyData = [];

        // Initialize all 12 months for the selected year
        for (let month = 1; month <= 12; month++) {
            yearlyData.push({
                date: `${month}월`,
                fullDate: `${year}-${month.toString().padStart(2, '0')}-01`,
                매출: 0,
                총수익: 0,
                취소금액: 0,
                예약수익: 0,
                티켓수익: 0,
                옵션수익: 0
            });
        }

        if (!apiData || apiData.length === 0) {
            // If no data, return empty date range
            return yearlyData;
        }

        // Aggregate data by month
        apiData
            .filter(item => moment(item.date).year() === year)
            .forEach(item => {
                const monthIndex = moment(item.date).month(); // 0-based (0=January, 11=December)
                if (monthIndex >= 0 && monthIndex < 12) {
                    const totalRevenue = (item.total_amount_info?.total_reserv || 0) +
                        (item.total_amount_info?.total_ticket || 0) +
                        (item.total_amount_info?.total_reserv_option || 0);
                    const totalCanceled = (item.total_amount_info?.total_cancel_reserv || 0) +
                        (item.total_amount_info?.total_cancel_ticket || 0) +
                        (item.total_amount_info?.total_cancel_reserv_option || 0);

                    yearlyData[monthIndex].매출 += item.total_amount || 0;
                    yearlyData[monthIndex].총수익 += totalRevenue;
                    yearlyData[monthIndex].취소금액 += totalCanceled;
                    yearlyData[monthIndex].예약수익 += item.total_amount_info?.total_reserv || 0;
                    yearlyData[monthIndex].티켓수익 += item.total_amount_info?.total_ticket || 0;
                    yearlyData[monthIndex].옵션수익 += item.total_amount_info?.total_reserv_option || 0;
                }
            });

        return yearlyData;
    };

    const getChartData = () => {
        if (viewType === 'yearly') {
            return processYearlyData(dataList, selectedYear);
        } else if (viewType === 'custom' && customDateRange.length === 2) {
            return processCustomRangeData(dataList, customDateRange[0], customDateRange[1]);
        } else {
            return processApiData(dataList, selectedRange);
        }
    };

    const [chartData, setChartData] = useState([]);

    const onDateChange = (val) => {
        const _start_date = normalizeDayStart(new Date()).subtract(val.value - 1, "days").toDate()
        const _end_date = normalizeDayEnd(new Date()).toDate()

        setSelectedRange(val.value);
        const newChartData = processApiData(dataList, val.value);
        setChartData(newChartData);
        onChange([_start_date, _end_date])
    }

    const onViewTypeChange = (val) => {
        setViewType(val.value);
        updateChartData(val.value);
    }

    const onCustomDateChange = (dates) => {
        setCustomDateRange(dates);
        if (dates.length === 2) {
            setChartData(processCustomRangeData(dataList, dates[0], dates[1]));
            onChange(dates);
        }
    }

    const onYearChange = (val) => {
        setSelectedYear(val.value);
        setChartData(processYearlyData(dataList, val.value));
        onChange([moment().year(val.value).startOf('year').toDate(), moment().year(val.value).endOf('year').toDate()]);
    }

    const updateChartData = (newViewType) => {
        let newData = [];
        if (newViewType === 'yearly') {
            newData = processYearlyData(dataList, selectedYear);
        } else if (newViewType === 'custom' && customDateRange.length === 2) {
            newData = processCustomRangeData(dataList, customDateRange[0], customDateRange[1]);
        } else {
            newData = processApiData(dataList, selectedRange);
        }
        setChartData(newData);
    }

    // Update chart data when view type changes
    useEffect(() => {
        if (dataList && dataList.length > 0) {
            updateChartData(viewType);
        }
    }, [viewType, dataList, selectedRange, customDateRange, selectedYear]);
    return (
        <div className="card card-height-100">
            <div className="card-header align-items-center d-flex">
                <h4 className="card-title mb-0 flex-grow-1">{title}</h4>
                <div className="d-flex flex-shrink-0 flex-row align-items-center gap-2">
                    <div><span>단위: {viewType === 'yearly' ? '월' : '일'}</span></div>
                    {viewSelectors.viewType && <div>
                        <span>보기: </span>
                        <Select
                            className="mb-0"
                            style={{ minWidth: '120px' }}
                            value={viewTypeOptions.find(item => item.value === viewType)}
                            onChange={onViewTypeChange}
                            options={viewTypeOptions}
                            id={"view-type-select"}
                        />
                    </div>}
                    {viewSelectors.dateRange && <div>
                        {viewType === 'preset' && (
                            <div>
                                <span>범위: </span>
                                <Select
                                    className="mb-0"
                                    value={selectDateRange.find(item => item.value === selectedRange)}
                                    onChange={onDateChange}
                                    options={selectDateRange}
                                    id={"choices-single-chart-range"}
                                />
                            </div>
                        )}
                        {viewType === 'custom' && (
                            <div>
                                <span>기간: </span>
                                <CommonDatePicker
                                    className="form-control"
                                    style={{ minWidth: '200px' }}
                                    options={{
                                        mode: "range",
                                        defaultDate: customDateRange
                                    }}
                                    placeholder="날짜 선택"
                                    value={customDateRange}
                                    onChange={onCustomDateChange}
                                />
                            </div>
                        )}
                        {viewType === 'yearly' && (
                            <div>
                                <span>연도: </span>
                                <Select
                                    className="mb-0"
                                    style={{ minWidth: '100px' }}
                                    value={yearOptions.find(item => item.value === selectedYear)}
                                    onChange={onYearChange}
                                    options={yearOptions}
                                    id={"year-select"}
                                />
                            </div>
                        )}
                    </div>}

                    {viewSelectors.showDetails && <Button
                        
                        onClick={() => setShowDetails(!showDetails)}
                     color="outline-primary" size="sm">
                        {showDetails ? '간단히 보기' : '상세히 보기'}
                    </Button>}
                </div>
            </div>
            <div className="card-body px-0">
                <div id="revenue-expenses-charts" dir="ltr">
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                            data={chartData}
                            margin={{
                                top: 20,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 12 }}
                                interval={chartData.length > 0 ? Math.floor(chartData.length / 10) || 0 : 0}
                            />
                            <YAxis
                                tick={{ fontSize: 12 }}
                                tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                            />
                            <Tooltip
                                formatter={(value, name) => [
                                    `${value.toLocaleString()}원`,
                                    name
                                ]}
                                labelFormatter={(label) => {
                                    if (viewType === 'yearly') {
                                        return `${selectedYear}년 ${label}`;
                                    }
                                    return `날짜: ${label}`;
                                }}
                            />
                            <Legend />
                            <Bar dataKey="매출" fill="#007bff" name="매출" />
                            {showDetails && (
                                <>
                                    <Bar dataKey="총수익" fill="#28a745" name="총수익" />
                                    <Bar dataKey="취소금액" fill="#dc3545" name="취소금액" />
                                    <Bar dataKey="예약수익" fill="#17a2b8" name="예약수익" />
                                    <Bar dataKey="티켓수익" fill="#ffc107" name="티켓수익" />
                                    <Bar dataKey="옵션수익" fill="#6f42c1" name="옵션수익" />
                                </>
                            )}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default BalanceOverview;
